# FDA Labels Search API

Search FDA drug labels (SPL) with full-text search and filtering.

## Endpoint

```
GET /v1/fda-label/search
```

## Quick Start

<LanguageBlock>
  <Block language="node">
    ```typescript
    import { Fhirfly } from "@fhirfly-io/terminology";

    const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

    // Search for acetaminophen labels
    const results = await client.fdaLabel.search({
      q: "acetaminophen"
    });

    console.log(`Found ${results.total} labels`);
    for (const item of results.items) {
      console.log(`${item.name} (${item.product_type})`);
    }
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?q=acetaminophen"
    ```
  </Block>
</LanguageBlock>

## Parameters

### Text Search

| Parameter | Description | Example |
|-----------|-------------|---------|
| `q` | General text search across all fields | `q=tylenol` |
| `name` | Search by product name | `name=acetaminophen` |
| `brand` | Search by brand name | `brand=tylenol` |
| `generic` | Search by generic name | `generic=acetaminophen` |
| `substance` | Search by active ingredient | `substance=ibuprofen` |
| `manufacturer` | Search by manufacturer name | `manufacturer=johnson` |

### Filters

| Parameter | Type | Description | Example |
|-----------|------|-------------|---------|
| `product_type` | string | OTC, prescription, or cellular | `otc`, `rx`, `cellular` |
| `route` | string | Administration route | `oral`, `topical`, `intravenous` |
| `pharm_class` | string | Pharmacologic class | `Nonsteroidal Anti-inflammatory` |
| `rxcui` | string | RxNorm concept ID | `rxcui=313782` |
| `ndc` | string | National Drug Code | `ndc=0363-0225` |
| `application_number` | string | FDA application number | `application_number=NDA020503` |
| `has_rxcui` | boolean | Has linked RxNorm codes | `true`, `false` |
| `is_current` | boolean | Current labels only (default: true) | `true`, `false` |

### Pagination & Response

| Parameter | Default | Max | Description |
|-----------|---------|-----|-------------|
| `limit` | 20 | 100 | Results per page |
| `page` | 1 | 100 | Page number |
| `shape` | compact | — | `compact`, `standard`, `full` |

## Example Response

```json
{
  "facets": {
    "manufacturer": {
      "Johnson & Johnson Consumer Inc.": 12,
      "Novartis Consumer Health, Inc.": 8
    },
    "pharm_class_epc": {
      "Nonsteroidal Anti-inflammatory Drug [EPC]": 15,
      "Analgesic [EPC]": 10
    },
    "product_type": {
      "otc": 45,
      "rx": 12
    },
    "route": {
      "oral": 40,
      "topical": 10
    }
  },
  "has_more": true,
  "items": [
    {
      "id": "d1e5f6a7-8b9c-4d0e-a1b2-c3d4e5f6a7b8",
      "name": "TYLENOL Extra Strength",
      "brand_name": "TYLENOL",
      "generic_name": "ACETAMINOPHEN",
      "product_type": "otc",
      "route": ["oral"],
      "manufacturer": "Johnson & Johnson Consumer Inc."
    },
    {
      "id": "a2b3c4d5-e6f7-8a9b-0c1d-2e3f4a5b6c7d",
      "name": "Acetaminophen Tablets",
      "brand_name": null,
      "generic_name": "ACETAMINOPHEN",
      "product_type": "otc",
      "route": ["oral"],
      "manufacturer": "Major Pharmaceuticals"
    }
  ],
  "limit": 2,
  "meta": {
    "legal": {
      "attribution_required": false,
      "citation": "FDA Structured Product Labeling (SPL). Accessed 2026-01-28 via FHIRfly.",
      "license": "public_domain",
      "source_name": "FDA SPL"
    }
  },
  "page": 1,
  "total": 57,
  "total_capped": false
}
```

## Use Cases

### Find OTC Pain Relievers

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      q: "pain relief",
      product_type: "otc"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?q=pain%20relief&product_type=otc"
    ```
  </Block>
</LanguageBlock>

### Find Prescription Drugs by Manufacturer

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      manufacturer: "pfizer",
      product_type: "rx"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?manufacturer=pfizer&product_type=rx"
    ```
  </Block>
</LanguageBlock>

### Find Labels by NDC

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      ndc: "0363-0225"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?ndc=0363-0225"
    ```
  </Block>
</LanguageBlock>

### Find Labels with RxNorm Linkage

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      q: "metformin",
      has_rxcui: true
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?q=metformin&has_rxcui=true"
    ```
  </Block>
</LanguageBlock>

### Search by Pharmacologic Class

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      pharm_class: "Proton Pump Inhibitor"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?pharm_class=Proton%20Pump%20Inhibitor"
    ```
  </Block>
</LanguageBlock>

### Find Topical Medications

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.fdaLabel.search({
      route: "topical",
      product_type: "otc"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/fda-label/search?route=topical&product_type=otc"
    ```
  </Block>
</LanguageBlock>

## Facets

FDA Label search returns these facets:

| Facet | Description |
|-------|-------------|
| `product_type` | Distribution by OTC/Rx/cellular |
| `route` | Distribution by administration route |
| `pharm_class_epc` | Distribution by established pharmacologic class |
| `manufacturer` | Top manufacturers in results |

## Valid Values

### Product Type Values

- `otc` — Over-the-counter drugs
- `rx` — Prescription drugs
- `cellular` — Cellular/gene therapy products

### Common Route Values

- `oral` — Oral administration
- `topical` — Applied to skin
- `intravenous` — IV injection
- `intramuscular` — IM injection
- `subcutaneous` — Subcutaneous injection
- `inhalation` — Inhaled
- `ophthalmic` — Eye drops
- `otic` — Ear drops
- `nasal` — Nasal spray
- `rectal` — Rectal administration
- `transdermal` — Skin patch

## Required Scope

`fda-label.search`

## See Also

- [FDA Labels Lookup API](/docs/value-apis/fda-labels) — Look up a specific FDA label
- [NDC Search API](/docs/search-apis/ndc) — Search drug codes
- [RxNorm Search API](/docs/search-apis/rxnorm) — Search drug terminology
- [Search APIs Overview](/docs/search-apis/overview) — Common search features
